import React, { useMemo, useState } from "react";
import { Download } from "lucide-react";
import logoMark from "../assets/logo-mark.png";
import { APP_IDS } from "../constants/testIds";
import { api, formatApiError } from "../api/client";

// Teal chrome bar matching the design-system reference. Uses CSS variables
// so it adapts to dark mode / high-contrast automatically.
export const Header = ({ year, onYearChange, user, onLogout, onOpenProfile, currentYear, historyYears = [], isReadOnly = false, showToast }) => {
  const [downloading, setDownloading] = useState(false);
  const years = useMemo(() => {
    const set = new Set();
    if (currentYear) set.add(currentYear);
    for (const y of historyYears) set.add(y);
    if (set.size === 0) {
      const base = year || new Date().getFullYear();
      set.add(base);
    }
    return Array.from(set).sort((a, b) => b - a);
  }, [currentYear, historyYears, year]);

  const initials = (user?.name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join("");

  const canDownloadCsv = user?.role === "admin" && isReadOnly && !!year;
  const downloadYearCsv = async () => {
    if (!canDownloadCsv || downloading) return;
    setDownloading(true);
    try {
      // Axios `blob` response so we can trigger a browser download regardless
      // of the browser's own MIME sniffing. Auth header is added by the shared
      // interceptor in api/client.js.
      const { data } = await api.get(`/admin/history/${year}/buyers.csv`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `400club-buyers-${year}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast?.(`${year} buyer CSV downloaded`);
    } catch (e) {
      showToast?.(formatApiError(e) || "Could not download CSV");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <header
      style={{
        background: "var(--brand-primary)",
        color: "var(--ink-on-brand)",
        borderBottom: "1px solid var(--brand-primary-active)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <img
            src={logoMark}
            alt=""
            width={44}
            height={44}
            className="rounded-md flex-shrink-0 object-contain"
          />
          <div className="min-w-0">
            <h1
              className="leading-tight truncate"
              style={{
                fontFamily: "var(--font-display)",
                fontSize: "var(--text-heading)",
                fontWeight: "var(--weight-bold)",
                color: "var(--ink-on-brand)",
                letterSpacing: "var(--tracking-tight)",
              }}
            >
              Four Hundred Club
            </h1>
            <p
              className="truncate"
              style={{
                fontSize: "var(--text-caption)",
                color: "rgba(255,255,255,0.75)",
                marginTop: 2,
              }}
            >
              Red Hook Rhinebeck Elks Lodge #2022 · {year}
              {isReadOnly && (
                <span
                  data-testid="header-readonly-badge"
                  className="ml-2 inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                  style={{ background: "var(--semantic-warning-soft)", color: "#7A5100" }}
                >
                  Read-only
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <select
            data-testid={APP_IDS.yearSelect}
            value={year}
            onChange={(e) => onYearChange(Number(e.target.value))}
            aria-label="Select year"
            className="rounded-md px-3 py-1.5 text-sm font-medium focus:outline-none"
            style={{
              background: "rgba(255,255,255,0.12)",
              color: "var(--ink-on-brand)",
              border: "1px solid rgba(255,255,255,0.20)",
              fontFamily: "var(--font-body)",
              transition: "background-color var(--duration-base) var(--ease-standard)",
            }}
          >
            {years.map((y) => {
              const isCurrent = y === currentYear;
              const label = isCurrent ? `${y} (current)` : y;
              return <option key={y} value={y} style={{ background: "var(--surface-card)", color: "var(--ink-primary)" }}>{label}</option>;
            })}
          </select>

          {canDownloadCsv && (
            <button
              type="button"
              onClick={downloadYearCsv}
              disabled={downloading}
              data-testid="header-download-year-csv"
              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold focus:outline-none disabled:opacity-60"
              style={{
                background: "rgba(255,255,255,0.12)",
                color: "var(--ink-on-brand)",
                border: "1px solid rgba(255,255,255,0.20)",
                transition: "background-color var(--duration-base) var(--ease-standard)",
              }}
              title={`Download the ${year} buyer list as CSV`}
              aria-label={`Download ${year} buyer CSV`}
            >
              <Download className="h-3.5 w-3.5" />
              {downloading ? "Preparing…" : `Download ${year} CSV`}
            </button>
          )}

          {user && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onOpenProfile}
                data-testid="header-profile-btn"
                className="flex items-center gap-2.5 rounded-full pr-3 pl-1 py-1 focus:outline-none"
                title="Edit your profile"
                aria-label="Open your profile"
                style={{
                  background: "rgba(255,255,255,0.10)",
                  border: "1px solid rgba(255,255,255,0.16)",
                  transition: "background-color var(--duration-base) var(--ease-standard)",
                }}
              >
                <span
                  className="inline-flex items-center justify-center rounded-full font-bold text-xs"
                  style={{
                    width: 28,
                    height: 28,
                    background: "var(--brand-accent)",
                    color: "var(--ink-on-brand)",
                  }}
                >
                  {initials}
                </span>
                <span className="text-left leading-tight">
                  <span className="block text-sm font-semibold" data-testid="header-user-name" style={{ color: "var(--ink-on-brand)" }}>
                    {user.name}
                  </span>
                  <span
                    className="block text-[10px] uppercase font-bold tracking-widest"
                    style={{ color: user.role === "admin" ? "var(--brand-accent-soft)" : "rgba(255,255,255,0.72)" }}
                    data-testid="header-user-role"
                  >
                    {user.role}
                  </span>
                </span>
              </button>
              <button
                data-testid="logout-btn"
                onClick={onLogout}
                className="px-3 py-1.5 text-xs font-semibold rounded-md focus:outline-none"
                style={{
                  background: "transparent",
                  color: "var(--ink-on-brand)",
                  border: "1px solid rgba(255,255,255,0.35)",
                  transition: "background-color var(--duration-base) var(--ease-standard)",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.12)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
